import { useCallback, useEffect, useRef } from 'react'

import { type GroupContext, type UserContext } from '../types/context'

import { v4 as uuidv4 } from 'uuid'

import { enrichEventProperties } from '../utils/enrichment'

export const SESSION_STORAGE_KEY = 'app_insights_session_id'
export const SESSION_START_TIME_KEY = 'app_insights_session_start_time'
export const SESSION_ACCUMULATED_TIME_KEY = 'app_insights_session_accumulated_time'
export const SESSION_INITIALIZED_KEY = 'app_insights_session_initialized'

export function useSessionTracking(
  appInsights: any,
  isEnabled: boolean,
  isDebugMode: boolean,
  userContext: UserContext,
  groupContext: GroupContext,
  sessionHeartbeatInterval: number
) {
  // Helper to get session data from storage
  const getSessionData = () => {
    try {
      const id = sessionStorage.getItem(SESSION_STORAGE_KEY)
      const startTimeStr = sessionStorage.getItem(SESSION_START_TIME_KEY)
      const accumulatedTimeStr = sessionStorage.getItem(SESSION_ACCUMULATED_TIME_KEY)
      const initializedStr = sessionStorage.getItem(SESSION_INITIALIZED_KEY)

      return {
        id: id || null,
        startTime: startTimeStr ? parseInt(startTimeStr, 10) : null,
        accumulatedTime: accumulatedTimeStr ? parseInt(accumulatedTimeStr, 10) : 0,
        initialized: initializedStr === 'true'
      }
    } catch {
      return { id: null, startTime: null, accumulatedTime: 0, initialized: false }
    }
  }

  // Helper to save session data to storage
  const saveSessionData = (data: { id?: string; startTime?: number; accumulatedTime?: number; initialized?: boolean }) => {
    try {
      if (data.id !== undefined) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, data.id)
      }

      if (data.startTime !== undefined) {
        sessionStorage.setItem(SESSION_START_TIME_KEY, data.startTime.toString())
      }

      if (data.accumulatedTime !== undefined) {
        sessionStorage.setItem(SESSION_ACCUMULATED_TIME_KEY, data.accumulatedTime.toString())
      }

      if (data.initialized !== undefined) {
        sessionStorage.setItem(SESSION_INITIALIZED_KEY, data.initialized.toString())
      }
    } catch {
      // Silent fail for private browsing mode
    }
  }

  // Get stored session data or create new session
  const storedSession = getSessionData()

  // Initialize refs from session storage - don't overwrite existing values
  const sessionId = useRef<string>(storedSession.id || uuidv4())
  const sessionStartTime = useRef<number>(storedSession.startTime || Date.now())
  const lastActivityTime = useRef<number>(Date.now())
  const accumulatedSessionTime = useRef<number>(storedSession.accumulatedTime || 0)
  const isSessionActive = useRef<boolean>(false)
  const sessionInitialized = useRef<boolean>(storedSession.initialized || false)
  const heartbeatInterval = useRef<NodeJS.Timeout | null>(null)

  // Only create session storage if it doesn't exist
  useEffect(() => {
    if (!storedSession.id) {
      saveSessionData({
        id: sessionId.current,
        startTime: sessionStartTime.current,
        accumulatedTime: accumulatedSessionTime.current,
        initialized: sessionInitialized.current
      })
    }
  }, [storedSession.id])

  const startSession = useCallback(() => {
    // If session is already active, just return the ID
    if (isSessionActive.current) return sessionId.current

    // If session was previously initialized (exists in storage), just resume it
    if (sessionInitialized.current) {
      isSessionActive.current = true
      lastActivityTime.current = Date.now()

      if (isDebugMode) {
        console.group('🔍 App Insights: Session Resumed After Page Reload')
        console.log('Session ID:', sessionId.current)
        console.groupEnd()
      }

      // Only track a "SessionResumed" event, not a new session start
      if (isEnabled) {
        appInsights.trackEvent({
          name: 'SessionResumed',
          properties: enrichEventProperties(
            {
              sessionId: sessionId.current,
              originalStartTime: sessionStartTime.current,
              accumulatedPauseTime: accumulatedSessionTime.current
            },
            userContext,
            groupContext
          )
        })
      }

      return sessionId.current
    }

    // This is a truly new session - initialize everything
    sessionInitialized.current = true
    isSessionActive.current = true
    lastActivityTime.current = Date.now()

    // Only set these values if we're starting a brand new session
    if (!storedSession.id) {
      sessionStartTime.current = Date.now()
      accumulatedSessionTime.current = 0
    }

    // Save to session storage
    saveSessionData({
      id: sessionId.current,
      startTime: sessionStartTime.current,
      initialized: true,
      accumulatedTime: accumulatedSessionTime.current
    })

    // Track session start event
    const sessionProps = {
      sessionId: sessionId.current,
      isNewSession: true
    }

    if (isEnabled) {
      appInsights.trackEvent({
        name: 'SessionStart',
        properties: enrichEventProperties(sessionProps, userContext, groupContext)
      })

      if (isDebugMode) {
        console.group('🔍 App Insights: Session Started')
        console.log('Session ID:', sessionId.current)
        console.log('Start Time:', new Date(sessionStartTime.current).toISOString())
        console.groupEnd()
      }
    }

    return sessionId.current
  }, [appInsights, isEnabled, isDebugMode, userContext, groupContext, storedSession.id])

  const endSession = useCallback(
    (reason = 'manual') => {
      if (!isSessionActive.current) return

      isSessionActive.current = false

      // Calculate total session duration
      const endTime = Date.now()
      const sessionDuration = endTime - sessionStartTime.current

      // Calculate active session time
      const activeSessionTime = sessionDuration - accumulatedSessionTime.current

      // Reset accumulated time
      accumulatedSessionTime.current = 0
      saveSessionData({ accumulatedTime: 0 })

      // If we're ending the session completely, reset the initialized flag
      if (reason === 'manual' || reason === 'telemetry_disabled') {
        sessionInitialized.current = false
        saveSessionData({
          initialized: false,
          // Also clear the session ID and start time to force a new session next time
          id: '',
          startTime: 0
        })
      }

      // Track session end event
      const sessionProps = {
        sessionId: sessionId.current,
        totalDuration: sessionDuration,
        totalDurationSeconds: Math.round(sessionDuration / 1000),
        activeDuration: activeSessionTime,
        activeDurationSeconds: Math.round(activeSessionTime / 1000),
        endReason: reason
      }

      if (isEnabled) {
        appInsights.trackEvent({
          name: 'SessionEnd',
          properties: enrichEventProperties(sessionProps, userContext, groupContext)
        })

        // Also track as a metric for easier analysis
        appInsights.trackMetric({
          name: 'SessionDuration',
          average: activeSessionTime / 1000, // in seconds
          properties: enrichEventProperties(
            {
              sessionId: sessionId.current,
              endReason: reason
            },
            userContext,
            groupContext
          )
        })

        if (isDebugMode) {
          console.group('🔍 App Insights: Session Ended')
          console.log('Session ID:', sessionId.current)
          console.log('Total Duration:', sessionDuration, 'ms')
          console.log('Active Duration:', activeSessionTime, 'ms')
          console.log('End Reason:', reason)
          console.groupEnd()
        }
      }
    },
    [appInsights, isEnabled, isDebugMode, userContext, groupContext]
  )

  const pauseSession = useCallback(() => {
    if (!isSessionActive.current) return

    const currentTime = Date.now()
    const additionalTime = currentTime - lastActivityTime.current
    accumulatedSessionTime.current += additionalTime

    // Save accumulated time to session storage
    saveSessionData({ accumulatedTime: accumulatedSessionTime.current })

    if (isEnabled && isDebugMode) {
      console.log('🔍 App Insights: Session Paused', {
        sessionId: sessionId.current,
        accumulatedTime: accumulatedSessionTime.current,
        additionalTime: additionalTime
      })
    }
  }, [isEnabled, isDebugMode])

  const resumeSession = useCallback(() => {
    if (!isSessionActive.current) {
      startSession()
    } else {
      lastActivityTime.current = Date.now()

      if (isEnabled && isDebugMode) {
        console.log('🔍 App Insights: Session Resumed', {
          sessionId: sessionId.current,
          accumulatedPauseTime: accumulatedSessionTime.current
        })
      }
    }
  }, [isEnabled, isDebugMode, startSession])

  const sendSessionHeartbeat = useCallback(() => {
    if (!isSessionActive.current || !isEnabled) return

    const currentTime = Date.now()
    const totalElapsed = currentTime - sessionStartTime.current
    const activeTime = totalElapsed - accumulatedSessionTime.current

    const heartbeatProps = {
      sessionId: sessionId.current,
      totalElapsedTime: totalElapsed,
      activeTime: activeTime,
      accumulatedPauseTime: accumulatedSessionTime.current
    }

    appInsights.trackEvent({
      name: 'SessionHeartbeat',
      properties: enrichEventProperties(heartbeatProps, userContext, groupContext)
    })

    lastActivityTime.current = currentTime

    if (isDebugMode) {
      console.log('🔍 App Insights: Session Heartbeat', heartbeatProps)
    }
  }, [appInsights, isEnabled, isDebugMode, userContext, groupContext])

  // Calculate and return current session duration
  const getSessionDuration = useCallback(() => {
    if (!isSessionActive.current || !sessionStartTime.current) return 0

    const currentTime = Date.now()
    const totalElapsed = currentTime - sessionStartTime.current
    const activeTime = totalElapsed - accumulatedSessionTime.current

    return {
      total: totalElapsed,
      active: activeTime,
      paused: accumulatedSessionTime.current
    }
  }, [])

  // Set up session heartbeat interval
  useEffect(() => {
    if (!isEnabled) return

    heartbeatInterval.current = setInterval(sendSessionHeartbeat, sessionHeartbeatInterval)

    return () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current)
      }
    }
  }, [isEnabled, sendSessionHeartbeat, sessionHeartbeatInterval])

  // Handle visibility change to pause/resume session
  useEffect(() => {
    if (!isEnabled) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        pauseSession()
      } else if (document.visibilityState === 'visible') {
        resumeSession()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [isEnabled, pauseSession, resumeSession])

  // Handle beforeunload event to save session state
  useEffect(() => {
    if (!isEnabled) return

    const handleBeforeUnload = () => {
      pauseSession()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isEnabled, pauseSession])

  return {
    sessionId,
    isSessionActive,
    startSession,
    endSession,
    pauseSession,
    resumeSession,
    getSessionId: () => sessionId.current,
    isSessionActiveValue: () => isSessionActive.current,
    getSessionDuration
  }
}
